import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import ButtonGradient from "./assets/svg/ButtonGradient";
import ScrollToTop from './components/ScrollToTop';
import SEO from './components/SEO';
import PromoBanner from './components/Banner/PromoBanner';

import Hero from "./components/Hero";
import Services from "./components/Services";
import Footer from "./components/Footer";
import Pricing from "./components/Pricing";

import Vision from "./pages/Vision/Vision";
import Header from "./components/Navbar/Header";
import Faq from "./components/Faq";
import Careers from './pages/Careers';
import Legal from './pages/Legal';
import DrawerMenu from './components/DrawerMenu'; 
import Contact from './pages/Contact';
import Accessibility from './pages/Vision/Accessibilty';
import KnowledgeScreen from './pages/Vision/Knowledge';
import EmpowermentScreen from './pages/Vision/Empowerment';
import SeeHowItWorks from './pages/SeeHowItWorks';
import CreditComparison from './components/CreditComparison';
import CreditComparisonDetail from './pages/CreditComparisonDetail';
import PriceComparison from './components/Price-Comparison';
import ComparisonSection from './components/ComparisonSection';
import AboutUs from './pages/About/AboutUs';
import SubHero from './components/Hero/SubHero';
import FuteurCred from './pages/Monitoring/FuteurCred';
import TermsAndCondition from './pages/TermsAndCondition';
import PrivacyPolicy from './pages/PrivacyPolicy';

import 'bootstrap/dist/css/bootstrap.min.css';
import SeeSample from './pages/SeeSamples/SeeSample';

const App = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Track page view with Google Analytics
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname,
        page_title: document.title
      });
    }
  }, [location]);

  return (
    <HelmetProvider>
      <PromoBanner />
      <DrawerMenu />
      <ScrollToTop />

      <Routes>
        {/* Main landing page */}
        <Route
          path="/"
          element={
            <>
              <SEO
                title="Futeur AI - Revolutionizing Financial Technology"
                description="Experience the future of financial technology with Futeur AI. Advanced credit monitoring, price comparisons, and innovative financial solutions."
                canonicalUrl="/"
              />
              <Header />
              <Hero />
              <SubHero />
              <Pricing />
              <PriceComparison />
              <CreditComparison />
              <ComparisonSection />
              <Services />
              <Faq />
              <Footer />
            </>
          }
        />

        {/* Redirect old paths to new consistent paths */}
        <Route path="/credit-comparsion" element={<Navigate to="/credit-comparison" replace />} />
        <Route path="/CreditComparison" element={<Navigate to="/credit-comparison" replace />} />
        <Route path="/about-futeur" element={<Navigate to="/about" replace />} />
        <Route path="/SeeSample" element={<Navigate to="/samples" replace />} />
        
        {/* Main routes with proper casing and SEO */}
        <Route path="/credit-comparison" element={
          <>
            <SEO
              title="Credit Comparison"
              description="Compare credit options and make informed financial decisions with Futeur AI's advanced comparison tools."
              canonicalUrl="/credit-comparison"
            />
            <CreditComparisonDetail />
          </>
        } />
        
        <Route path="/about" element={
          <>
            <SEO
              title="About Futeur AI"
              description="Learn about Futeur AI's mission to revolutionize financial technology and empower users with innovative solutions."
              canonicalUrl="/about"
            />
            <AboutUs />
          </>
        } />
        
        <Route path="/careers" element={
          <>
            <SEO
              title="Careers at Futeur AI"
              description="Join our team at Futeur AI and help shape the future of financial technology."
              canonicalUrl="/careers"
            />
            <Careers />
          </>
        } />
        
        <Route path="/legal" element={
          <>
            <SEO
              title="Legal Information"
              description="Important legal information and policies for Futeur AI services."
              canonicalUrl="/legal"
            />
            <Legal />
          </>
        } />

        <Route path="/FuteurCred" element={
          <>
            <SEO
              title="Futeur Credit Monitoring"
              description="Advanced credit monitoring and analysis tools by Futeur AI."
              canonicalUrl="/FuteurCred"
            />
            <FuteurCred />
          </>
        } />

        <Route path="/SeeHowItWorks" element={
          <>
            <SEO
              title="See How Futeur Works"
              description="Learn how Futeur AI's innovative financial technology solutions work."
              canonicalUrl="/SeeHowItWorks"
            />
            <SeeHowItWorks />
          </>
        } />

        <Route path="/knowledge" element={
          <>
            <SEO
              title="Futeur Knowledge Base"
              description="Explore Futeur AI's knowledge base and learn about our innovative financial solutions."
              canonicalUrl="/knowledge"
            />
            <KnowledgeScreen />
          </>
        } />
        
        <Route path="/empowerment" element={
          <>
            <SEO
              title="Empowerment Through Futeur"
              description="Discover how Futeur AI empowers users with innovative financial technology solutions."
              canonicalUrl="/empowerment"
            />
            <EmpowermentScreen />
          </>
        } />
        
        <Route path="/contact" element={
          <>
            <SEO
              title="Contact Futeur AI"
              description="Get in touch with Futeur AI for support, inquiries, or partnerships."
              canonicalUrl="/contact"
            />
            <Contact />
          </>
        } />
        
        <Route path="/vision" element={
          <>
            <SEO
              title="Our Vision"
              description="Discover Futeur AI's vision for the future of financial technology."
              canonicalUrl="/vision"
            />
            <Vision />
          </>
        } />
        
        <Route path="/terms-and-conditions" element={
          <>
            <SEO
              title="Terms and Conditions"
              description="Read our Terms and Conditions to understand the rules and guidelines for using Futeur AI services."
              canonicalUrl="/terms-and-conditions"
            />
            <TermsAndCondition />
          </>
        } />
        <Route path="/privacy-policy" element={
          <>
            <SEO
              title="Privacy Policy"
              description="Read our Privacy Policy to understand how we handle your personal data."
              canonicalUrl="/privacy-policy"
            />
            <PrivacyPolicy/>
          </>
        } />
        
        <Route path="/accessibility" element={
          <>
            <SEO
              title="Accessibility"
              description="Futeur AI's commitment to accessibility and inclusive design."
              canonicalUrl="/accessibility"
            />
            <Accessibility />
          </>
        } />

        {/* Redirect inconsistent casing */}
        <Route path="/Knowledge" element={<Navigate to="/knowledge" replace />} />
        <Route path="/Empowerment" element={<Navigate to="/empowerment" replace />} />
        
        <Route path="/how-it-works" element={
          <>
            <SEO
              title="How It Works"
              description="Learn how Futeur AI's innovative financial technology solutions work."
              canonicalUrl="/how-it-works"
            />
            <SeeHowItWorks />
          </>
        } />
        
        <Route path="/samples" element={
          <>
            <SEO
              title="Sample Reports"
              description="View sample reports and demonstrations of Futeur AI's capabilities."
              canonicalUrl="/samples"
            />
            <SeeSample />
          </>
        } />

        {/* Catch all route for 404s */}
        <Route path="*" element={
          <>
            <SEO
              title="Page Not Found"
              description="The requested page could not be found."
              canonicalUrl="/404"
              noindex={true}
            />
            <div className="text-center py-5">
              <h1>404 - Page Not Found</h1>
              <p>The page you&apos;re looking for doesn&apos;t exist.</p>
            </div>
          </>
        } />
      </Routes>

      <ButtonGradient />
    </HelmetProvider>
  );
};

export default App;
